import { canvas, drawSpriteRect, gl } from "./gl.js";
import {
  appState,
  autoPlayState,
  boardAnim,
  autoToggleLabel,
  gameState,
  languageState,
  moveState,
  rosterState,
  runtimeState,
  selectionState,
  turnState,
  yutAnim,
  yutButtonAnim,
  yutButtonIconSprite,
  yutButtonLabel,
  yutButtonGlowSprite,
  yutButtonTexture,
  yutButtonTitleSprite,
  turnBanner,
  turnBannerSprites,
  oneMoreOverlay,
  oneMoreSprites,
  yutResultOverlay,
  yutResultSprites,
  yutSprites,
  yutThrowState,
  seasonState,
  YUT_ANIM_FPS,
  YUT_ANIM_HEIGHT,
  YUT_BUTTON_COUNT_Y,
  YUT_BUTTON_TITLE_HEIGHT,
  YUT_BUTTON_TITLE_Y,
  YUT_RESULT_DURATION,
  YUT_RESULT_FADE,
  YUT_RESULT_HEIGHT,
  YUT_RESULT_HOLD,
  YUT_RESULT_OFFSET_Y,
  ONE_MORE_DURATION,
  ONE_MORE_FADE,
  ONE_MORE_HEIGHT,
  ONE_MORE_HOLD,
} from "./state.js";
import { clamp, lerp, getSpriteSourceSize } from "./utils.js";
import { getAutoToggleRect, getYutButtonRect } from "./ui.js";
import { isPiecesAnimating } from "./pieces.js";
import { playSfx } from "./sfx.js";

export function isYutAnimating() {
  return (
    yutAnim.state === "throw" ||
    yutAnim.state === "result" ||
    (yutAnim.state === "hold" && !yutAnim.moveQueued)
  );
}

export function isYutResultOverlayAnimating() {
  return yutResultOverlay.active;
}

export function isOneMoreOverlayAnimating() {
  return oneMoreOverlay.active;
}

export function isYutButtonAnimating() {
  return yutButtonAnim.active;
}

export function isTurnBannerAnimating() {
  return turnBanner.active;
}

export function isPlayerThrowReady() {
  if (gameState.winner || seasonState.ended) {
    return false;
  }
  if (runtimeState.actionBusy) {
    return false;
  }
  if (!boardAnim.initialized || boardAnim.active || rosterState.spawnPending) {
    return false;
  }
  if (!runtimeState.currentPrompt || runtimeState.currentPrompt.type !== "throw") {
    return false;
  }
  const remainingThrows = Math.max(0, yutThrowState.total - yutThrowState.used);
  if (remainingThrows <= 0 && !hasFreeThrow("player")) {
    return false;
  }
  if (yutAnim.state === "throw" || yutAnim.state === "result") {
    return false;
  }
  if (moveState.active || selectionState.active || isPiecesAnimating()) {
    return false;
  }
  return true;
}

export function updateYutAnimation(dt, panelRect) {
  if (!isYutAnimating()) {
    return;
  }
  yutAnim.elapsed += dt;
  const frameDuration = 1 / YUT_ANIM_FPS;
  const frameCount = yutAnim.frames ? yutAnim.frames.length : 0;
  while (yutAnim.elapsed >= frameDuration) {
    yutAnim.elapsed -= frameDuration;
    yutAnim.frame += 1;
    if (frameCount && yutAnim.frame >= frameCount) {
      if (yutAnim.state === "throw") {
        selectYutResult();
      } else if (yutAnim.state === "result") {
        yutAnim.state = "hold";
        yutAnim.frame = Math.max(0, frameCount - 1);
        yutAnim.elapsed = 0;
        startYutResultOverlay(yutAnim.resultKey);
        break;
      }
    }
  }

  if (yutAnim.state === "hold" && !yutAnim.moveQueued) {
    yutAnim.moveQueued = true;
    appState.onYutHold?.();
    return;
  }
}

export function renderYutAnimation(boardRect) {
  if (yutAnim.state === "idle" || !yutAnim.frames || yutAnim.frames.length === 0) {
    return;
  }
  const frameSprite = yutAnim.frames[Math.min(yutAnim.frame, yutAnim.frames.length - 1)];
  if (!frameSprite || !frameSprite.texture) {
    return;
  }
  const frameSize = getSpriteSourceSize(frameSprite);
  const height = boardRect.height * YUT_ANIM_HEIGHT;
  const width = height * (frameSize.width / frameSize.height);
  const center = {
    x: boardRect.left + boardRect.width * 0.5,
    y: boardRect.bottom + boardRect.height * 0.5,
  };
  const rect = {
    left: center.x - width * 0.5,
    bottom: center.y - height * 0.5,
    width,
    height,
  };
  drawSpriteRect(frameSprite, rect, [1, 1, 1, 1]);
}

export function updateYutResultOverlay(dt) {
  if (!yutResultOverlay.active) {
    return;
  }
  if (yutResultOverlay.elapsed < YUT_RESULT_DURATION) {
    yutResultOverlay.elapsed += dt;
    if (yutResultOverlay.elapsed >= YUT_RESULT_DURATION) {
      yutResultOverlay.elapsed = YUT_RESULT_DURATION;
    }
    return;
  }
  yutResultOverlay.holdElapsed += dt;
  if (yutResultOverlay.holdElapsed >= YUT_RESULT_HOLD + YUT_RESULT_FADE) {
    yutResultOverlay.active = false;
  }
}

export function updateOneMoreOverlay(dt) {
  if (!oneMoreOverlay.active) {
    return;
  }
  if (oneMoreOverlay.elapsed < ONE_MORE_DURATION) {
    oneMoreOverlay.elapsed += dt;
    if (oneMoreOverlay.elapsed >= ONE_MORE_DURATION) {
      oneMoreOverlay.elapsed = ONE_MORE_DURATION;
    }
    return;
  }
  oneMoreOverlay.holdElapsed += dt;
  if (oneMoreOverlay.holdElapsed >= ONE_MORE_HOLD + ONE_MORE_FADE) {
    oneMoreOverlay.active = false;
  }
}

export function updateTurnBanner(dt) {
  if (!turnBanner.active) {
    return;
  }
  const sprite = getTurnBannerSprite();
  if (!sprite || !sprite.texture) {
    return;
  }
  if (turnBanner.elapsed < TURN_BANNER_DURATION) {
    turnBanner.elapsed += dt;
    if (turnBanner.elapsed >= TURN_BANNER_DURATION) {
      turnBanner.elapsed = TURN_BANNER_DURATION;
    }
    return;
  }
  turnBanner.holdElapsed += dt;
  if (turnBanner.holdElapsed >= TURN_BANNER_HOLD + TURN_BANNER_FADE) {
    turnBanner.active = false;
  }
}

export function renderYutResultOverlay(boardRect) {
  if (!yutResultOverlay.sprite || !yutResultOverlay.sprite.texture) {
    return;
  }
  if (!yutResultOverlay.active) {
    return;
  }
  const t = yutResultOverlay.elapsed / YUT_RESULT_DURATION;
  const scale = getYutResultScale(t);
  let alpha = 1;
  if (yutResultOverlay.holdElapsed > YUT_RESULT_HOLD) {
    const fadeT = (yutResultOverlay.holdElapsed - YUT_RESULT_HOLD) / YUT_RESULT_FADE;
    alpha = 1 - clamp(fadeT, 0, 1);
  }
  const height = boardRect.height * YUT_RESULT_HEIGHT * scale;
  const spriteSize = getSpriteSourceSize(yutResultOverlay.sprite);
  const width = height * (spriteSize.width / spriteSize.height);
  const center = {
    x: boardRect.left + boardRect.width * 0.5,
    y: boardRect.bottom + boardRect.height * (0.5 + YUT_RESULT_OFFSET_Y),
  };
  const rect = {
    left: center.x - width * 0.5,
    bottom: center.y - height * 0.5,
    width,
    height,
  };
  drawSpriteRect(yutResultOverlay.sprite, rect, [1, 1, 1, alpha]);
}

export function renderOneMoreOverlay(boardRect) {
  const sprite = getOneMoreSprite();
  if (!sprite || !sprite.texture || !oneMoreOverlay.active) {
    return;
  }
  const t = oneMoreOverlay.elapsed / ONE_MORE_DURATION;
  const scale = getYutResultScale(t) * getOneMoreScaleBoost();
  let alpha = 1;
  if (oneMoreOverlay.holdElapsed > ONE_MORE_HOLD) {
    const fadeT = (oneMoreOverlay.holdElapsed - ONE_MORE_HOLD) / ONE_MORE_FADE;
    alpha = 1 - clamp(fadeT, 0, 1);
  }
  const spriteSize = getSpriteSourceSize(sprite);
  const height = boardRect.height * ONE_MORE_HEIGHT * scale;
  const width = height * (spriteSize.width / spriteSize.height);
  const center = {
    x: boardRect.left + boardRect.width * 0.5,
    y: boardRect.bottom + boardRect.height * 0.5,
  };
  const rect = {
    left: center.x - width * 0.5,
    bottom: center.y - height * 0.5,
    width,
    height,
  };
  drawSpriteRect(sprite, rect, [1, 1, 1, alpha]);
}

export function renderTurnBanner(panelRect) {
  if (!turnBanner.active) {
    return;
  }
  const sprite = getTurnBannerSprite();
  if (!sprite || !sprite.texture) {
    return;
  }
  const t = turnBanner.elapsed / TURN_BANNER_DURATION;
  const scale = getTurnBannerScale(t);
  let alpha = 1;
  if (turnBanner.holdElapsed > TURN_BANNER_HOLD) {
    const fadeT = (turnBanner.holdElapsed - TURN_BANNER_HOLD) / TURN_BANNER_FADE;
    alpha = 1 - clamp(fadeT, 0, 1);
  }
  const langScale =
    languageState.current === "en" ? TURN_BANNER_SCALE_EN : TURN_BANNER_SCALE_KO;
  const baseHeight = panelRect.height * TURN_BANNER_HEIGHT * langScale;
  const height = baseHeight * scale;
  const spriteSize = getSpriteSourceSize(sprite);
  const width = height * (spriteSize.width / spriteSize.height);
  const marginX = panelRect.width * 0.03;
  const marginY = panelRect.height * 0.03;
  const offsetY = panelRect.height * TURN_BANNER_OFFSET_Y;
  const center = {
    x: panelRect.left + panelRect.width - marginX - width * 0.5,
    y: panelRect.bottom + panelRect.height - marginY - height * 0.5 - offsetY,
  };
  const rect = {
    left: center.x - width * 0.5,
    bottom: center.y - height * 0.5,
    width,
    height,
  };
  drawSpriteRect(sprite, rect, [1, 1, 1, alpha]);
}

export function startYutThrowAnimationFromServer(action) {
  if (!action) {
    return false;
  }
  if (!action.key) {
    return false;
  }
  if (!yutSprites.throwFrames[0] || !yutSprites.throwFrames[0].texture) {
    return false;
  }
  if (yutAnim.state === "throw" || yutAnim.state === "result") {
    return false;
  }
  if (gameState.winner) {
    return false;
  }
  if (moveState.active || selectionState.active || isPiecesAnimating()) {
    return false;
  }
  yutAnim.resultKey = action.key;
  yutAnim.resultSteps = Number(action.steps) || 0;
  yutAnim.state = "throw";
  yutAnim.frames = yutSprites.throwFrames;
  yutAnim.frame = 0;
  yutAnim.elapsed = 0;
  yutAnim.owner = action.owner || "player";
  yutAnim.moveQueued = false;
  yutAnim.scoreAwarded = false;
  playSfx("yut_throw");
  appState.requestDraw?.();
  return true;
}

export function updateYutButtonAnimation(dt) {
  if (!yutButtonAnim.active) {
    return;
  }
  yutButtonAnim.progress += dt / yutButtonAnim.duration;
  if (yutButtonAnim.progress >= 1) {
    yutButtonAnim.progress = 1;
    yutButtonAnim.active = false;
  }
}

export function startYutButtonDrop() {
  yutButtonAnim.pending = false;
  yutButtonAnim.active = true;
  yutButtonAnim.progress = 0;
}

export function renderYutButton(panelRect) {
  if (!yutButtonTexture.texture) {
    return;
  }
  const rect = getYutButtonRect(panelRect);
  const isPlayerTurn = turnState.current === "player" && !gameState.winner && !seasonState.ended;
  const target = isPlayerTurn ? 1 : 0.55;
  if (typeof yutButtonAnim.tint !== "number") {
    yutButtonAnim.tint = target;
  }
  yutButtonAnim.tint = lerp(yutButtonAnim.tint, target, 0.06);
  const tint = [yutButtonAnim.tint, yutButtonAnim.tint, yutButtonAnim.tint, 1];
  drawSpriteRect(yutButtonTexture, rect, tint);
  if (yutButtonTitleSprite.texture) {
    const spriteSize = getSpriteSourceSize(yutButtonTitleSprite);
    const height = rect.height * YUT_BUTTON_TITLE_HEIGHT;
    const width = height * (spriteSize.width / spriteSize.height);
    const center = {
      x: rect.left + rect.width * 0.5,
      y: rect.bottom + rect.height * YUT_BUTTON_TITLE_Y,
    };
    const titleRect = {
      left: center.x - width * 0.5,
      bottom: center.y - height * 0.5,
      width,
      height,
    };
    drawSpriteRect(yutButtonTitleSprite, titleRect, tint);
  }
  const remainingThrows = Math.max(0, yutThrowState.total - yutThrowState.used);
  const isLocked = remainingThrows <= 0 && !hasFreeThrow("player");
  if (isLocked) {
    drawSpriteRect(yutButtonTexture, rect, [0, 0, 0, 0.4]);
  }
  const labelTint = isLocked ? [1, 1, 1, 1] : tint;
  if (yutButtonLabel.texture) {
    drawSpriteRect(yutButtonLabel, rect, labelTint);
  }
  if (yutButtonIconSprite.texture && yutButtonLabel.countIcon) {
    const icon = yutButtonLabel.countIcon;
    const labelHeight = yutButtonLabel.height;
    const iconRect = {
      left: rect.left + (icon.left / yutButtonLabel.width) * rect.width,
      bottom: rect.bottom + ((labelHeight - icon.bottom - icon.height) / labelHeight) * rect.height,
      width: (icon.width / yutButtonLabel.width) * rect.width,
      height: (icon.height / yutButtonLabel.height) * rect.height,
    };
    drawSpriteRect(yutButtonIconSprite, iconRect, labelTint);
  }
}

export function initAutoToggleLabel() {
  autoToggleLabel.canvas = document.createElement("canvas");
  autoToggleLabel.canvas.width = autoToggleLabel.width;
  autoToggleLabel.canvas.height = autoToggleLabel.height;
  autoToggleLabel.ctx = autoToggleLabel.canvas.getContext("2d");
  updateAutoToggleLabelTexture();
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      updateAutoToggleLabelTexture();
      appState.requestDraw?.();
    });
  }
}

export function setAutoPlayEnabled(next) {
  autoPlayState.enabled = !!next;
  updateAutoToggleLabelTexture();
  appState.requestDraw?.();
}

export function toggleAutoPlay() {
  setAutoPlayEnabled(!autoPlayState.enabled);
}

export function renderAutoToggle(panelRect) {
  if (!autoToggleLabel.texture) {
    return;
  }
  const rect = getAutoToggleRect(panelRect);
  ensureAutoToggleLabelSize(rect.width, rect.height);
  drawSpriteRect(autoToggleLabel, rect, [1, 1, 1, 1]);
}

function offsetRect(rect, offset) {
  return {
    left: rect.left + offset.x,
    bottom: rect.bottom + offset.y,
    width: rect.width,
    height: rect.height,
  };
}

function getGlowRect(rect) {
  if (!yutButtonGlowSprite.texture) {
    return rect;
  }
  const glowSize = getSpriteSourceSize(yutButtonGlowSprite);
  const buttonSize = getSpriteSourceSize(yutButtonTexture);
  const scale =
    buttonSize.width > 0 && glowSize.width > 0 ? glowSize.width / buttonSize.width : 1;
  const centerX = rect.left + rect.width * 0.5;
  const centerY = rect.bottom + rect.height * 0.5;
  const width = rect.width * scale;
  const height = rect.height * scale;
  return {
    left: centerX - width * 0.5,
    bottom: centerY - height * 0.5,
    width,
    height,
  };
}

function ensureAutoToggleLabelSize(width, height) {
  if (!autoToggleLabel.canvas) {
    return;
  }
  const targetWidth = Math.max(1, Math.round(width));
  const targetHeight = Math.max(1, Math.round(height));
  if (autoToggleLabel.canvas.width === targetWidth && autoToggleLabel.canvas.height === targetHeight) {
    return;
  }
  autoToggleLabel.canvas.width = targetWidth;
  autoToggleLabel.canvas.height = targetHeight;
  autoToggleLabel.ctx = autoToggleLabel.canvas.getContext("2d");
  updateAutoToggleLabelTexture();
}

export function initYutButtonLabel() {
  yutButtonLabel.canvas = document.createElement("canvas");
  yutButtonLabel.canvas.width = yutButtonLabel.width;
  yutButtonLabel.canvas.height = yutButtonLabel.height;
  yutButtonLabel.ctx = yutButtonLabel.canvas.getContext("2d");
  updateYutButtonLabelTexture();
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      updateYutButtonLabelTexture();
      appState.requestDraw?.();
    });
  }
}

export function updateYutThrowUI() {
  updateYutButtonLabelTexture();
  appState.requestDraw?.();
}

export function startTurnBanner(team) {
  turnBanner.team = team;
  turnBanner.active = true;
  turnBanner.elapsed = 0;
  turnBanner.holdElapsed = 0;
  appState.requestDraw?.();
}

function updateYutButtonLabelTexture() {
  if (!yutButtonLabel.ctx) {
    return;
  }
  const ctx = yutButtonLabel.ctx;
  const width = yutButtonLabel.canvas.width;
  const height = yutButtonLabel.canvas.height;
  ctx.clearRect(0, 0, width, height);

  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  ctx.shadowColor = "rgba(0, 0, 0, 0)";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  const fontSize = Math.round(height * 0.16);
  ctx.font = `700 ${fontSize}px NanumSquareB`;
  const remaining = Math.max(0, yutThrowState.total - yutThrowState.used);
  const isLocked = remaining <= 0 && !hasFreeThrow("player");
  const text = `${remaining}`;
  const metrics = ctx.measureText(text);
  const paddingX = Math.max(6, fontSize * 0.25);
  const paddingY = Math.max(4, fontSize * 0.14);
  const boxHeight = fontSize + paddingY * 2;
  const iconSize = getSpriteSourceSize(yutButtonIconSprite);
  const iconHeight = fontSize * 1.72;
  const iconWidth = iconHeight * (iconSize.width / iconSize.height);
  const iconGap = Math.max(4, fontSize * 0.25);
  const contentWidth = iconWidth + iconGap + metrics.width;
  const boxWidth = contentWidth + paddingX * 2;
  const centerX = width * 0.5;
  const centerY = height * YUT_BUTTON_COUNT_Y;
  const boxLeft = centerX - boxWidth * 0.5;
  const boxBottom = centerY - boxHeight * 0.5;
  const radius = Math.min(boxHeight * 0.45, boxWidth * 0.25);
  yutButtonLabel.countBox = {
    left: boxLeft,
    bottom: boxBottom,
    width: boxWidth,
    height: boxHeight,
  };
  const iconOffsetY = boxHeight * 0.08;
  const iconOffsetX = iconWidth * 1.0;
  yutButtonLabel.countIcon = {
    left: boxLeft - iconGap - iconWidth + iconOffsetX,
    bottom: centerY - iconHeight * 0.5 + iconOffsetY,
    width: iconWidth,
    height: iconHeight,
  };
  ctx.fillStyle = "rgba(30, 30, 30, 0.85)";
  ctx.strokeStyle = "rgba(60, 60, 60, 0.9)";
  ctx.lineWidth = Math.max(1, Math.round(fontSize * 0.1));
  drawRoundedRect(ctx, boxLeft, boxBottom, boxWidth, boxHeight, radius);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = isLocked ? "#FF5236" : "#ffffff";
  const textX = boxLeft + paddingX + iconWidth + iconGap + metrics.width * 0.5;
  ctx.fillText(text, textX, centerY);

  if (!yutButtonLabel.texture) {
    yutButtonLabel.texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, yutButtonLabel.texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  } else {
    gl.bindTexture(gl.TEXTURE_2D, yutButtonLabel.texture);
  }
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, yutButtonLabel.canvas);
}

function updateAutoToggleLabelTexture() {
  if (!autoToggleLabel.ctx) {
    return;
  }
  const ctx = autoToggleLabel.ctx;
  const width = autoToggleLabel.canvas.width;
  const height = autoToggleLabel.canvas.height;
  ctx.clearRect(0, 0, width, height);

  const radius = height * 0.12;
  ctx.fillStyle = "#2f2a24";
  drawRoundedRect(ctx, 0, 0, width, height, radius);
  ctx.fill();

  const padding = height * 0.18;
  const boxSize = height * 0.64;
  const boxX = padding;
  const boxY = (height - boxSize) * 0.5;
  ctx.fillStyle = "#1c1a17";
  drawRoundedRect(ctx, boxX, boxY, boxSize, boxSize, boxSize * 0.12);
  ctx.fill();

  ctx.lineWidth = Math.max(1, height * 0.06);
  ctx.strokeStyle = "rgba(255, 255, 255, 0.18)";
  ctx.stroke();

  if (autoPlayState.enabled) {
    ctx.strokeStyle = "#f4d17f";
    ctx.lineWidth = Math.max(2, height * 0.12);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(boxX + boxSize * 0.25, boxY + boxSize * 0.55);
    ctx.lineTo(boxX + boxSize * 0.45, boxY + boxSize * 0.72);
    ctx.lineTo(boxX + boxSize * 0.78, boxY + boxSize * 0.3);
    ctx.stroke();
  }

  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#ffffff";
  const fontSize = Math.round(height * 0.42);
  ctx.font = `700 ${fontSize}px NanumSquareB`;
  const textLeft = boxX + boxSize + padding * 0.6;
  const textWidth = width - textLeft - padding;
  const autoText = languageState.current === "en" ? "AUTO" : "\uc790\ub3d9";
  ctx.fillText(autoText, textLeft + textWidth * 0.5, height * 0.52);

  if (!autoToggleLabel.texture) {
    autoToggleLabel.texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, autoToggleLabel.texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  } else {
    gl.bindTexture(gl.TEXTURE_2D, autoToggleLabel.texture);
  }
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, autoToggleLabel.canvas);
}

function drawRoundedRect(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, width * 0.5, height * 0.5);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}


function startYutResultOverlay(key) {
  if (!key) {
    yutResultOverlay.sprite = null;
    yutResultOverlay.active = false;
    yutResultOverlay.elapsed = 0;
    yutResultOverlay.holdElapsed = 0;
    return;
  }
  yutResultOverlay.sprite = yutResultSprites[key] || null;
  yutResultOverlay.active = !!(yutResultOverlay.sprite && yutResultOverlay.sprite.texture);
  yutResultOverlay.elapsed = 0;
  yutResultOverlay.holdElapsed = 0;
}

export function startOneMoreOverlay(team, variant = 1) {
  oneMoreOverlay.active = true;
  oneMoreOverlay.elapsed = 0;
  oneMoreOverlay.holdElapsed = 0;
  oneMoreOverlay.team = team;
  oneMoreOverlay.variant = Math.min(4, Math.max(1, Math.round(variant)));
}

const TURN_BANNER_DURATION = 0.35;
const TURN_BANNER_HOLD = 2.0;
const TURN_BANNER_FADE = 1.0;
const TURN_BANNER_HEIGHT = 0.12;
const TURN_BANNER_OFFSET_Y = 0.035;
const TURN_BANNER_SCALE_EN = 0.5;
const TURN_BANNER_SCALE_KO = 0.9;

function getYutResultScale(t) {
  const clamped = clamp(t, 0, 1);
  const segment = clamped * 3;
  const index = Math.min(2, Math.floor(segment));
  const local = segment - index;
  const keys = [2.0, 0.9, 1.1, 1.0];
  return lerp(keys[index], keys[index + 1], local);
}

function getTurnBannerScale(t) {
  const clamped = clamp(t, 0, 1);
  const segment = clamped * 3;
  const index = Math.min(2, Math.floor(segment));
  const local = segment - index;
  const keys = [2.0, 0.9, 1.1, 1.0];
  return lerp(keys[index], keys[index + 1], local);
}

function getTurnBannerSprite() {
  return turnBanner.team === "monster" ? turnBannerSprites.monster : turnBannerSprites.player;
}

function selectYutResult() {
  let result = null;
  if (yutAnim.resultKey) {
    result = yutSprites.results.find((entry) => entry.key === yutAnim.resultKey) || null;
  }
  if (!result) {
    if (runtimeState.debugAllowed) {
      console.warn("[yut] Missing server result key:", yutAnim.resultKey);
    }
    yutAnim.state = "idle";
    yutAnim.frames = null;
    yutAnim.frame = 0;
    yutAnim.elapsed = 0;
    yutAnim.resultSteps = 0;
    yutAnim.resultKey = null;
    yutAnim.moveQueued = false;
    if (appState.onYutHold) {
      const handler = appState.onYutHold;
      appState.onYutHold = null;
      handler();
    }
    return;
  }
  yutAnim.state = "result";
  yutAnim.frames = result ? result.frames : null;
  yutAnim.frame = 0;
  yutAnim.elapsed = 0;
  yutAnim.resultSteps = result ? result.steps : 0;
  yutAnim.resultKey = result ? result.key : null;
  yutAnim.moveQueued = false;
  if (result) {
    if (result.key === "yut") {
      playSfx("yut_rare");
    } else if (result.key === "mo") {
      playSfx("yut_legendary");
    } else {
      playSfx("yut_normal");
    }
  }
}

function hasFreeThrow(owner) {
  return owner === "player" && turnState.freeThrowFor === "player";
}

function getOneMoreSprite() {
  const variant = Math.min(4, Math.max(1, Math.round(oneMoreOverlay.variant || 1)));
  if (variant === 2) {
    return oneMoreSprites.two;
  }
  if (variant === 3) {
    return oneMoreSprites.three;
  }
  if (variant >= 4) {
    return oneMoreSprites.four;
  }
  return oneMoreSprites.one;
}

function getOneMoreScaleBoost() {
  const variant = Math.min(4, Math.max(1, Math.round(oneMoreOverlay.variant || 1)));
  if (variant === 2) {
    return 1.2;
  }
  if (variant === 3) {
    return 1.4;
  }
  if (variant >= 4) {
    return 1.6;
  }
  return 1.0;
}
